import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { TemplateRef, Directive, ContentChild, EventEmitter, Input, Output, ContentChildren, Component, ChangeDetectionStrategy, ViewChild, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { ReactWrapperComponent, AngularReact, registerElement } from '@angular-react/core';
import { omit, Styled } from '@angular-react/fabric/lib/utils';
import { ChangeableItemDirective, ChangeableItemsDirective, mergeItemChanges } from '@angular-react/fabric/lib/components/core';
import { GroupItemDirective, FabGroupModule } from '@angular-react/fabric/lib/components/group';
import { CommonModule } from '@angular/common';
import { DetailsList } from '@fluentui/react/lib/DetailsList';

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Wrapper directive for rendering a custom column to a DetailsListColumn
 */
class DetailsListColumnRenderDirective {
}
/** @nocollapse */ DetailsListColumnRenderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListColumnRenderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ DetailsListColumnRenderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: DetailsListColumnRenderDirective, selector: "fab-details-list-column > render", queries: [{ propertyName: "templateRef", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListColumnRenderDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-details-list-column > render' }]
        }], propDecorators: { templateRef: [{
                type: ContentChild,
                args: [TemplateRef]
            }] } });
/**
 * Wrapper directive for creating a DetailsListColumn
 */
class DetailsListColumnDirective extends ChangeableItemDirective {
    constructor() {
        super(...arguments);
        // Callback members
        this.onColumnClick = new EventEmitter();
        this.onColumnContextMenu = new EventEmitter();
        this.onColumnResize = new EventEmitter();
    }
    ngAfterContentInit() {
        if (this.renderDirective && this.renderDirective.templateRef) {
            this.render = this.renderDirective.templateRef;
        }
    }
}
/** @nocollapse */ DetailsListColumnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListColumnDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ DetailsListColumnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: DetailsListColumnDirective, selector: "fab-details-list-column", inputs: { name: "name", fieldName: "fieldName", className: "className", minWidth: "minWidth", ariaLabel: "ariaLabel", isRowHeader: "isRowHeader", maxWidth: "maxWidth", columnActionsMode: "columnActionsMode", iconName: "iconName", isIconOnly: "isIconOnly", iconClassName: "iconClassName", isCollapsable: "isCollapsable", isSorted: "isSorted", isSortedDescending: "isSortedDescending", isResizable: "isResizable", isMultiline: "isMultiline", onRender: "onRender", getValueKey: "getValueKey", onRenderDivider: "onRenderDivider", isFiltered: "isFiltered", isGrouped: "isGrouped", data: "data", calculatedWidth: "calculatedWidth", currentWidth: "currentWidth", headerClassName: "headerClassName", isPadded: "isPadded", sortAscendingAriaLabel: "sortAscendingAriaLabel", sortDescendingAriaLabel: "sortDescendingAriaLabel", groupAriaLabel: "groupAriaLabel", filterAriaLabel: "filterAriaLabel", render: "render" }, outputs: { onColumnClick: "onColumnClick", onColumnContextMenu: "onColumnContextMenu", onColumnResize: "onColumnResize" }, queries: [{ propertyName: "renderDirective", first: true, predicate: DetailsListColumnRenderDirective, descendants: true }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListColumnDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-details-list-column' }]
        }], propDecorators: { renderDirective: [{
                type: ContentChild,
                args: [DetailsListColumnRenderDirective]
            }], name: [{
                type: Input
            }], fieldName: [{
                type: Input
            }], className: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], isRowHeader: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], columnActionsMode: [{
                type: Input
            }], iconName: [{
                type: Input
            }], isIconOnly: [{
                type: Input
            }], iconClassName: [{
                type: Input
            }], isCollapsable: [{
                type: Input
            }], isSorted: [{
                type: Input
            }], isSortedDescending: [{
                type: Input
            }], isResizable: [{
                type: Input
            }], isMultiline: [{
                type: Input
            }], onRender: [{
                type: Input
            }], getValueKey: [{
                type: Input
            }], onRenderDivider: [{
                type: Input
            }], isFiltered: [{
                type: Input
            }], isGrouped: [{
                type: Input
            }], data: [{
                type: Input
            }], calculatedWidth: [{
                type: Input
            }], currentWidth: [{
                type: Input
            }], headerClassName: [{
                type: Input
            }], isPadded: [{
                type: Input
            }], sortAscendingAriaLabel: [{
                type: Input
            }], sortDescendingAriaLabel: [{
                type: Input
            }], groupAriaLabel: [{
                type: Input
            }], filterAriaLabel: [{
                type: Input
            }], render: [{
                type: Input
            }], onColumnClick: [{
                type: Output
            }], onColumnContextMenu: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Wrapper directive for creating multiple DetailsListColumns
 */
class DetailsListColumnsDirective extends ChangeableItemsDirective {
    get items() {
        return this.directiveItems.map(directiveItem => (Object.assign(Object.assign({}, directiveItem), { onColumnClick: (ev, column) => {
                directiveItem.onColumnClick.emit({ ev: ev && ev.nativeEvent, column });
            }, onColumnContextMenu: (column, ev) => {
                directiveItem.onColumnContextMenu.emit({ column, ev: ev && ev.nativeEvent });
            }, onColumnResize: (width) => {
                directiveItem.onColumnResize.emit({ width });
            } })));
    }
}
/** @nocollapse */ DetailsListColumnsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListColumnsDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ DetailsListColumnsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: DetailsListColumnsDirective, selector: "fab-details-list > columns", queries: [{ propertyName: "directiveItems", predicate: DetailsListColumnDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListColumnsDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-details-list > columns' }]
        }], propDecorators: { directiveItems: [{
                type: ContentChildren,
                args: [DetailsListColumnDirective]
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Wrapper directive for creating multiple DetailsList Groups
 */
class DetailsListGroupsDirective extends ChangeableItemsDirective {
    get items() {
        return this.directiveItems.toArray();
    }
}
/** @nocollapse */ DetailsListGroupsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListGroupsDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ DetailsListGroupsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: DetailsListGroupsDirective, selector: "fab-details-list > groups", queries: [{ propertyName: "directiveItems", predicate: GroupItemDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DetailsListGroupsDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-details-list > groups' }]
        }], propDecorators: { directiveItems: [{
                type: ContentChildren,
                args: [GroupItemDirective]
            }] } });

let FabDetailsListComponent = class FabDetailsListComponent extends ReactWrapperComponent {
    constructor(elementRef, changeDetectorRef, renderer, ngZone) {
        super(elementRef, changeDetectorRef, renderer, { ngZone, setHostDisplay: true });
        // Callback members
        this.onActiveItemChanged = new EventEmitter();
        this.onColumnHeaderClick = new EventEmitter();
        this.onColumnHeaderContextMenu = new EventEmitter();
        this.onColumnResize = new EventEmitter();
        this.onDidUpdate = new EventEmitter();
        this.onItemContextMenu = new EventEmitter();
        this.onItemInvoked = new EventEmitter();
        this.onRowDidMount = new EventEmitter();
        this.onRowWillUnmount = new EventEmitter();
        this._subscriptions = [];
        // Bind this to access Angular component properties
        this.onActiveItemChangedHandler = this.onActiveItemChangedHandler.bind(this);
        this.onColumnHeaderClickHandler = this.onColumnHeaderClickHandler.bind(this);
        this.onColumnHeaderContextMenuHandler = this.onColumnHeaderContextMenuHandler.bind(this);
        this.onColumnResizeHandler = this.onColumnResizeHandler.bind(this);
        this.onDidUpdateHandler = this.onDidUpdateHandler.bind(this);
        this.onItemContextMenuHandler = this.onItemContextMenuHandler.bind(this);
        this.onItemInvokedHandler = this.onItemInvokedHandler.bind(this);
        this.onRowDidMountHandler = this.onRowDidMountHandler.bind(this);
        this.onRowWillUnmountHandler = this.onRowWillUnmountHandler.bind(this);
    }
    ngOnInit() {
        this.onRenderCheckbox = this.createRenderPropHandler(this.renderCheckbox);
        this.onRenderDetailsFooter = this.createRenderPropHandler(this.renderDetailsFooter);
        this.onRenderDetailsHeader = this.createRenderPropHandler(this.renderDetailsHeader);
        this.onRenderRow = this.createRenderPropHandler(this.renderRow);
        const missingItemRenderer = this.createInputJsxRenderer(this.renderMissingItem);
        this.onRenderMissingItem = (index, rowProps) => missingItemRenderer({ index, rowProps });
    }
    ngOnChanges(changes) {
        if (changes['columns'] &&
            changes['columns'].currentValue &&
            changes['columns'].previousValue !== changes['columns'].currentValue) {
            this._createTransformedColumns(changes['columns'].currentValue);
        }
        if (changes['groups'] &&
            changes['groups'].currentValue &&
            changes['groups'].previousValue !== changes['groups'].currentValue) {
            this._createTransformedGroups(changes['groups'].currentValue);
        }
        super.ngOnChanges(changes);
    }
    ngAfterContentInit() {
        if (this.columnsDirective) {
            this._initDirective(this.columnsDirective, 'columns');
        }
        if (this.groupsDirective) {
            this._initDirective(this.groupsDirective, 'groups');
        }
        super.ngAfterContentInit();
    }
    ngOnDestroy() {
        this._subscriptions.forEach(sub => sub.unsubscribe());
    }
    onActiveItemChangedHandler(item, index, ev) {
        this.onActiveItemChanged.emit({ item, index, ev: ev.nativeEvent });
    }
    onColumnHeaderClickHandler(ev, column) {
        this.onColumnHeaderClick.emit({ ev: ev.nativeEvent, column });
    }
    onColumnHeaderContextMenuHandler(column, ev) {
        this.onColumnHeaderContextMenu.emit({ column, ev: ev.nativeEvent });
    }
    onColumnResizeHandler(column, newWidth, columnIndex) {
        this.onColumnResize.emit({ column, newWidth, columnIndex });
    }
    onDidUpdateHandler(detailsList) {
        this.onDidUpdate.emit({ detailsList });
    }
    onItemContextMenuHandler(item, index, ev) {
        this.onItemContextMenu.emit({ item, index, ev });
    }
    onItemInvokedHandler(item, index, ev) {
        this.onItemInvoked.emit({ item, index, ev });
    }
    onRowDidMountHandler(item, index) {
        this.onRowDidMount.emit({ item, index });
    }
    onRowWillUnmountHandler(item, index) {
        this.onRowWillUnmount.emit({ item, index });
    }
    _initDirective(directive, propertyKey) {
        const transformItemsFunc = this._transformItemsFunction(directive);
        const setItems = (mapper) => {
            this[propertyKey] = mapper(this[propertyKey]);
            transformItemsFunc(this[propertyKey]);
            this.markForCheck();
        };
        setItems(() => directive.items);
        // Subscribe to adding/removing items
        this._subscriptions.push(directive.onItemsChanged.subscribe((newItems) => {
            setItems(() => newItems.map(directive => directive));
        }));
        // Subscribe for existing item changes
        this._subscriptions.push(directive.onChildItemChanged.subscribe(({ key, changes }) => {
            setItems(items => items.map(item => (item.key === key ? mergeItemChanges(item, changes) : item)));
            this.markForCheck();
        }));
    }
    _transformItemsFunction(directive) {
        if (directive instanceof DetailsListColumnsDirective) {
            return (newColumns) => this._createTransformedColumns(newColumns);
        }
        else if (directive instanceof DetailsListGroupsDirective) {
            return (newGroups) => this._createTransformedGroups(newGroups);
        }
        else {
            throw new Error('Invalid directive given');
        }
    }
    _createTransformedColumns(newColumns) {
        this.transformedColumns_ = newColumns.map(column => this._transformDetailsListColumnOptionsToProps(column));
    }
    _transformDetailsListColumnOptionsToProps(options) {
        const renderer = this.createInputJsxRenderer(options.render);
        return Object.assign({}, omit(options, 'render'), renderer &&
            {
                onRender: (item, index, column) => renderer({ item, index, column }),
            });
    }
    _createTransformedGroups(newGroups) {
        this.transformedGroups_ = newGroups;
    }
};
/** @nocollapse */ FabDetailsListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDetailsListComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ FabDetailsListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.1", type: FabDetailsListComponent, selector: "fab-details-list", inputs: { theme: "theme", styles: "styles", componentRef: "componentRef", setKey: "setKey", items: "items", listProps: "listProps", initialFocusedIndex: "initialFocusedIndex", className: "className", groupProps: "groupProps", indentWidth: "indentWidth", selection: "selection", selectionMode: "selectionMode", selectionPreservedOnEmptyClick: "selectionPreservedOnEmptyClick", selectionZoneProps: "selectionZoneProps", layoutMode: "layoutMode", checkboxVisibility: "checkboxVisibility", isHeaderVisible: "isHeaderVisible", constrainMode: "constrainMode", rowElementEventMap: "rowElementEventMap", getCellValueKey: "getCellValueKey", dragDropEvents: "dragDropEvents", viewport: "viewport", ariaLabelForListHeader: "ariaLabelForListHeader", ariaLabelForSelectAllCheckbox: "ariaLabelForSelectAllCheckbox", ariaLabelForSelectionColumn: "ariaLabelForSelectionColumn", getRowAriaLabel: "getRowAriaLabel", getRowAriaDescribedBy: "getRowAriaDescribedBy", getKey: "getKey", ariaLabel: "ariaLabel", checkButtonAriaLabel: "checkButtonAriaLabel", ariaLabelForGrid: "ariaLabelForGrid", shouldApplyApplicationRole: "shouldApplyApplicationRole", minimumPixelsForDrag: "minimumPixelsForDrag", compact: "compact", usePageCache: "usePageCache", onShouldVirtualize: "onShouldVirtualize", checkboxCellClassName: "checkboxCellClassName", enterModalSelectionOnTouch: "enterModalSelectionOnTouch", columnReorderOptions: "columnReorderOptions", getGroupHeight: "getGroupHeight", useReducedRowRenderer: "useReducedRowRenderer", cellStyleProps: "cellStyleProps", disableSelectionZone: "disableSelectionZone", enableUpdateAnimations: "enableUpdateAnimations", useFastIcons: "useFastIcons", skipViewportMeasures: "skipViewportMeasures", renderCheckbox: "renderCheckbox", renderDetailsFooter: "renderDetailsFooter", renderDetailsHeader: "renderDetailsHeader", renderMissingItem: "renderMissingItem", renderRow: "renderRow", columns: "columns", groups: "groups" }, outputs: { onActiveItemChanged: "onActiveItemChanged", onColumnHeaderClick: "onColumnHeaderClick", onColumnHeaderContextMenu: "onColumnHeaderContextMenu", onColumnResize: "onColumnResize", onDidUpdate: "onDidUpdate", onItemContextMenu: "onItemContextMenu", onItemInvoked: "onItemInvoked", onRowDidMount: "onRowDidMount", onRowWillUnmount: "onRowWillUnmount" }, queries: [{ propertyName: "columnsDirective", first: true, predicate: DetailsListColumnsDirective, descendants: true, static: true }, { propertyName: "groupsDirective", first: true, predicate: DetailsListGroupsDirective, descendants: true, static: true }], viewQueries: [{ propertyName: "reactNodeRef", first: true, predicate: ["reactNode"], descendants: true, static: true }], exportAs: ["fabDetailsList"], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
    <DetailsList
      #reactNode
      [items]="items"
      [ariaLabel]="ariaLabel"
      [ariaLabelForGrid]="ariaLabelForGrid"
      [ariaLabelForListHeader]="ariaLabelForListHeader"
      [ariaLabelForSelectAllCheckbox]="ariaLabelForSelectAllCheckbox"
      [ariaLabelForSelectionColumn]="ariaLabelForSelectionColumn"
      [cellStyleProps]="cellStyleProps"
      [checkButtonAriaLabel]="checkButtonAriaLabel"
      [checkboxCellClassName]="checkboxCellClassName"
      [checkboxVisibility]="checkboxVisibility"
      [className]="className"
      [columnReorderOptions]="columnReorderOptions"
      [columns]="transformedColumns_"
      [compact]="compact"
      [componentRef]="componentRef"
      [constrainMode]="constrainMode"
      [disableSelectionZone]="disableSelectionZone"
      [dragDropEvents]="dragDropEvents"
      [enableUpdateAnimations]="enableUpdateAnimations"
      [enterModalSelectionOnTouch]="enterModalSelectionOnTouch"
      [getCellValueKey]="getCellValueKey"
      [getGroupHeight]="getGroupHeight"
      [getKey]="getKey"
      [getRowAriaDescribedBy]="getRowAriaDescribedBy"
      [getRowAriaLabel]="getRowAriaLabel"
      [groupProps]="groupProps"
      [groups]="transformedGroups_"
      [indentWidth]="indentWidth"
      [initialFocusedIndex]="initialFocusedIndex"
      [isHeaderVisible]="isHeaderVisible"
      [layoutMode]="layoutMode"
      [listProps]="listProps"
      [minimumPixelsForDrag]="minimumPixelsForDrag"
      [rowElementEventMap]="rowElementEventMap"
      [selection]="selection"
      [selectionMode]="selectionMode"
      [selectionPreservedOnEmptyClick]="selectionPreservedOnEmptyClick"
      [selectionZoneProps]="selectionZoneProps"
      [setKey]="setKey"
      [shouldApplyApplicationRole]="shouldApplyApplicationRole"
      [skipViewportMeasures]="skipViewportMeasures"
      [styles]="styles"
      [theme]="theme"
      [useFastIcons]="useFastIcons"
      [usePageCache]="usePageCache"
      [useReducedRowRenderer]="useReducedRowRenderer"
      [viewport]="viewport"
      [RenderCheckbox]="renderCheckbox && onRenderCheckbox"
      [RenderDetailsFooter]="renderDetailsFooter && onRenderDetailsFooter"
      [RenderDetailsHeader]="renderDetailsHeader && onRenderDetailsHeader"
      [RenderMissingItem]="renderMissingItem && onRenderMissingItem"
      [RenderRow]="renderDetailsHeader && onRenderRow"
      [ActiveItemChanged]="onActiveItemChangedHandler"
      [ColumnHeaderClick]="onColumnHeaderClickHandler"
      [ColumnHeaderContextMenu]="onColumnHeaderContextMenuHandler"
      [ColumnResize]="onColumnResizeHandler"
      [DidUpdate]="onDidUpdateHandler"
      [ItemContextMenu]="onItemContextMenuHandler"
      [ItemInvoked]="onItemInvokedHandler"
      [RowDidMount]="onRowDidMountHandler"
      [RowWillUnmount]="onRowWillUnmountHandler"
      [ShouldVirtualize]="onShouldVirtualize"
    >
    </DetailsList>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
FabDetailsListComponent = __decorate([
    AngularReact(),
    Styled('FabDetailsListComponent')
], FabDetailsListComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDetailsListComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fab-details-list',
                    exportAs: 'fabDetailsList',
                    template: `
    <DetailsList
      #reactNode
      [items]="items"
      [ariaLabel]="ariaLabel"
      [ariaLabelForGrid]="ariaLabelForGrid"
      [ariaLabelForListHeader]="ariaLabelForListHeader"
      [ariaLabelForSelectAllCheckbox]="ariaLabelForSelectAllCheckbox"
      [ariaLabelForSelectionColumn]="ariaLabelForSelectionColumn"
      [cellStyleProps]="cellStyleProps"
      [checkButtonAriaLabel]="checkButtonAriaLabel"
      [checkboxCellClassName]="checkboxCellClassName"
      [checkboxVisibility]="checkboxVisibility"
      [className]="className"
      [columnReorderOptions]="columnReorderOptions"
      [columns]="transformedColumns_"
      [compact]="compact"
      [componentRef]="componentRef"
      [constrainMode]="constrainMode"
      [disableSelectionZone]="disableSelectionZone"
      [dragDropEvents]="dragDropEvents"
      [enableUpdateAnimations]="enableUpdateAnimations"
      [enterModalSelectionOnTouch]="enterModalSelectionOnTouch"
      [getCellValueKey]="getCellValueKey"
      [getGroupHeight]="getGroupHeight"
      [getKey]="getKey"
      [getRowAriaDescribedBy]="getRowAriaDescribedBy"
      [getRowAriaLabel]="getRowAriaLabel"
      [groupProps]="groupProps"
      [groups]="transformedGroups_"
      [indentWidth]="indentWidth"
      [initialFocusedIndex]="initialFocusedIndex"
      [isHeaderVisible]="isHeaderVisible"
      [layoutMode]="layoutMode"
      [listProps]="listProps"
      [minimumPixelsForDrag]="minimumPixelsForDrag"
      [rowElementEventMap]="rowElementEventMap"
      [selection]="selection"
      [selectionMode]="selectionMode"
      [selectionPreservedOnEmptyClick]="selectionPreservedOnEmptyClick"
      [selectionZoneProps]="selectionZoneProps"
      [setKey]="setKey"
      [shouldApplyApplicationRole]="shouldApplyApplicationRole"
      [skipViewportMeasures]="skipViewportMeasures"
      [styles]="styles"
      [theme]="theme"
      [useFastIcons]="useFastIcons"
      [usePageCache]="usePageCache"
      [useReducedRowRenderer]="useReducedRowRenderer"
      [viewport]="viewport"
      [RenderCheckbox]="renderCheckbox && onRenderCheckbox"
      [RenderDetailsFooter]="renderDetailsFooter && onRenderDetailsFooter"
      [RenderDetailsHeader]="renderDetailsHeader && onRenderDetailsHeader"
      [RenderMissingItem]="renderMissingItem && onRenderMissingItem"
      [RenderRow]="renderDetailsHeader && onRenderRow"
      [ActiveItemChanged]="onActiveItemChangedHandler"
      [ColumnHeaderClick]="onColumnHeaderClickHandler"
      [ColumnHeaderContextMenu]="onColumnHeaderContextMenuHandler"
      [ColumnResize]="onColumnResizeHandler"
      [DidUpdate]="onDidUpdateHandler"
      [ItemContextMenu]="onItemContextMenuHandler"
      [ItemInvoked]="onItemInvokedHandler"
      [RowDidMount]="onRowDidMountHandler"
      [RowWillUnmount]="onRowWillUnmountHandler"
      [ShouldVirtualize]="onShouldVirtualize"
    >
    </DetailsList>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.NgZone }]; }, propDecorators: { columnsDirective: [{
                type: ContentChild,
                args: [DetailsListColumnsDirective, { static: true }]
            }], groupsDirective: [{
                type: ContentChild,
                args: [DetailsListGroupsDirective, { static: true }]
            }], reactNodeRef: [{
                type: ViewChild,
                args: ['reactNode', { static: true }]
            }], theme: [{
                type: Input
            }], styles: [{
                type: Input
            }], componentRef: [{
                type: Input
            }], setKey: [{
                type: Input
            }], items: [{
                type: Input
            }], listProps: [{
                type: Input
            }], initialFocusedIndex: [{
                type: Input
            }], className: [{
                type: Input
            }], groupProps: [{
                type: Input
            }], indentWidth: [{
                type: Input
            }], selection: [{
                type: Input
            }], selectionMode: [{
                type: Input
            }], selectionPreservedOnEmptyClick: [{
                type: Input
            }], selectionZoneProps: [{
                type: Input
            }], layoutMode: [{
                type: Input
            }], checkboxVisibility: [{
                type: Input
            }], isHeaderVisible: [{
                type: Input
            }], constrainMode: [{
                type: Input
            }], rowElementEventMap: [{
                type: Input
            }], getCellValueKey: [{
                type: Input
            }], dragDropEvents: [{
                type: Input
            }], viewport: [{
                type: Input
            }], ariaLabelForListHeader: [{
                type: Input
            }], ariaLabelForSelectAllCheckbox: [{
                type: Input
            }], ariaLabelForSelectionColumn: [{
                type: Input
            }], getRowAriaLabel: [{
                type: Input
            }], getRowAriaDescribedBy: [{
                type: Input
            }], getKey: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], checkButtonAriaLabel: [{
                type: Input
            }], ariaLabelForGrid: [{
                type: Input
            }], shouldApplyApplicationRole: [{
                type: Input
            }], minimumPixelsForDrag: [{
                type: Input
            }], compact: [{
                type: Input
            }], usePageCache: [{
                type: Input
            }], onShouldVirtualize: [{
                type: Input
            }], checkboxCellClassName: [{
                type: Input
            }], enterModalSelectionOnTouch: [{
                type: Input
            }], columnReorderOptions: [{
                type: Input
            }], getGroupHeight: [{
                type: Input
            }], useReducedRowRenderer: [{
                type: Input
            }], cellStyleProps: [{
                type: Input
            }], disableSelectionZone: [{
                type: Input
            }], enableUpdateAnimations: [{
                type: Input
            }], useFastIcons: [{
                type: Input
            }], skipViewportMeasures: [{
                type: Input
            }], renderCheckbox: [{
                type: Input
            }], renderDetailsFooter: [{
                type: Input
            }], renderDetailsHeader: [{
                type: Input
            }], renderMissingItem: [{
                type: Input
            }], renderRow: [{
                type: Input
            }], onActiveItemChanged: [{
                type: Output
            }], onColumnHeaderClick: [{
                type: Output
            }], onColumnHeaderContextMenu: [{
                type: Output
            }], onColumnResize: [{
                type: Output
            }], onDidUpdate: [{
                type: Output
            }], onItemContextMenu: [{
                type: Output
            }], onItemInvoked: [{
                type: Output
            }], onRowDidMount: [{
                type: Output
            }], onRowWillUnmount: [{
                type: Output
            }], columns: [{
                type: Input
            }], groups: [{
                type: Input
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
const components = [
    DetailsListColumnDirective,
    DetailsListColumnRenderDirective,
    DetailsListColumnsDirective,
    DetailsListGroupsDirective,
    FabDetailsListComponent,
];
class FabDetailsListModule {
    constructor() {
        // Add any React elements to the registry (used by the renderer).
        registerElement('DetailsList', () => DetailsList);
    }
}
/** @nocollapse */ FabDetailsListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDetailsListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ FabDetailsListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDetailsListModule, declarations: [DetailsListColumnDirective,
        DetailsListColumnRenderDirective,
        DetailsListColumnsDirective,
        DetailsListGroupsDirective,
        FabDetailsListComponent], imports: [CommonModule, FabGroupModule], exports: [DetailsListColumnDirective,
        DetailsListColumnRenderDirective,
        DetailsListColumnsDirective,
        DetailsListGroupsDirective,
        FabDetailsListComponent, FabGroupModule] });
/** @nocollapse */ FabDetailsListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDetailsListModule, imports: [[CommonModule, FabGroupModule], FabGroupModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDetailsListModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FabGroupModule],
                    declarations: components,
                    exports: [...components, FabGroupModule],
                    schemas: [NO_ERRORS_SCHEMA],
                }]
        }], ctorParameters: function () { return []; } });

// Copyright (c) Microsoft Corporation. All rights reserved.

/**
 * Generated bundle index. Do not edit.
 */

export { DetailsListColumnDirective, DetailsListColumnRenderDirective, DetailsListColumnsDirective, DetailsListGroupsDirective, FabDetailsListComponent, FabDetailsListModule };
